// ---------------------------------------------------------------------------
// YouAreYou (uru) — Public Marketing Pages
//
// Static informational pages referenced in the Connection Request mockup
// header nav: "How it works", "Security", "Help/Support".
//
// Routes:
//   /how-it-works  → HowItWorksPage
//   /security      → SecurityPage
//   /support       → SupportPage
//   /privacy       → PrivacyPage
//   /terms         → TermsPage
//
// These are public pages — no authentication required.
//
// Design notes:
//   No dedicated mockups exist for these pages. They follow the design
//   system established across the app (Public Sans, primary blue, light
//   background) and use a shared layout wrapper (MarketingLayout) with
//   the public nav shown in the Connection Request mockup:
//     Logo (left) · How it works · Security · Help (center) · ☰ (right)
//   Footer matches the Connection Request mockup:
//     "Secure, Encrypted Identity Verification by YouAreYou"
//     Privacy Policy · Terms of Service · Contact Support
// ---------------------------------------------------------------------------

import { type ReactNode } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import Icon from "@/components/ui/Icon";
import BrandName from "@/components/ui/BrandName";
import BrandMark from "@/components/ui/BrandMark";
import { useAuth } from "@/hooks/useAuth";

// ---------------------------------------------------------------------------
// MarketingLayout — shared shell for all public marketing pages
// ---------------------------------------------------------------------------

export function MarketingLayout() {
  const { user } = useAuth();
  const isSignedIn = !!user;

  return (
    <div className="flex min-h-dvh flex-col bg-background-light">
      {/* Header — matches Connection Request mockup header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <BrandMark className="h-8 w-8" />
            <span className="text-lg font-bold">
              <BrandName />
            </span>
          </Link>

          {/* Center nav — desktop only */}
          <nav className="hidden items-center gap-6 md:flex">
            <NavLink
              to="/how-it-works"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-primary-500"
                    : "text-text-secondary hover:text-text-primary"
                }`
              }
            >
              How it works
            </NavLink>
            <NavLink
              to="/security"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-primary-500"
                    : "text-text-secondary hover:text-text-primary"
                }`
              }
            >
              Security
            </NavLink>
            <NavLink
              to="/support"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-primary-500"
                    : "text-text-secondary hover:text-text-primary"
                }`
              }
            >
              Help
            </NavLink>
          </nav>

          {/* Right side — auth-aware CTA + mobile menu */}
          <div className="flex items-center gap-3">
            {isSignedIn ? (
              <Link
                to="/dashboard"
                className="hidden rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 sm:inline-flex items-center gap-1.5"
              >
                <Icon name="dashboard" className="text-base" />
                Go to Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className="hidden rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 sm:inline-flex"
              >
                Sign In
              </Link>
            )}
            {/* Mobile */}
            <div className="flex items-center gap-4 md:hidden">
              {isSignedIn ? (
                <Link to="/dashboard" className="text-sm font-semibold text-primary-500">
                  Dashboard
                </Link>
              ) : (
                <Link to="/login" className="text-sm font-semibold text-primary-500">
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Mobile nav — horizontal scroll */}
        <div className="flex items-center gap-4 overflow-x-auto border-t border-gray-100 px-4 py-2 md:hidden">
          <NavLink
            to="/how-it-works"
            className={({ isActive }) =>
              `shrink-0 text-xs font-medium ${
                isActive ? "text-primary-500" : "text-text-secondary"
              }`
            }
          >
            How it works
          </NavLink>
          <NavLink
            to="/security"
            className={({ isActive }) =>
              `shrink-0 text-xs font-medium ${
                isActive ? "text-primary-500" : "text-text-secondary"
              }`
            }
          >
            Security
          </NavLink>
          <NavLink
            to="/support"
            className={({ isActive }) =>
              `shrink-0 text-xs font-medium ${
                isActive ? "text-primary-500" : "text-text-secondary"
              }`
            }
          >
            Help
          </NavLink>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer — matches Connection Request mockup footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
          <p className="text-center text-sm text-text-secondary">
            Secure, Encrypted Identity Verification by{" "}
            <BrandName />
          </p>
          <div className="mt-3 flex items-center justify-center gap-4 text-xs text-text-muted">
            <Link
              to="/privacy"
              className="transition-colors hover:text-text-secondary"
            >
              Privacy Policy
            </Link>
            <span>·</span>
            <Link
              to="/terms"
              className="transition-colors hover:text-text-secondary"
            >
              Terms of Service
            </Link>
            <span>·</span>
            <Link
              to="/support"
              className="transition-colors hover:text-text-secondary"
            >
              Contact Support
            </Link>
          </div>
          <p className="mt-4 text-center text-[11px] text-text-muted">
            &copy; {new Date().getFullYear()} UrU.io. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section helper components
// ---------------------------------------------------------------------------

function PageHero({
  icon,
  title,
  subtitle,
}: {
  icon: string;
  title: ReactNode;
  subtitle: ReactNode;
}) {
  return (
    <div className="bg-gradient-to-b from-primary-50/60 to-transparent pb-8 pt-12 text-center sm:pb-12 sm:pt-16">
      <div className="mx-auto max-w-2xl px-4">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500 shadow-lg shadow-primary-500/20">
          <Icon name={icon} className="text-3xl text-white" />
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
          {title}
        </h1>
        <p className="mx-auto mt-3 max-w-lg text-base text-text-secondary leading-relaxed sm:text-lg">
          {subtitle}
        </p>
      </div>
    </div>
  );
}

function SectionCard({
  icon,
  iconBg = "bg-primary-50",
  iconColor = "text-primary-500",
  title,
  children,
}: {
  icon: string;
  iconBg?: string;
  iconColor?: string;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-3 flex items-center gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconBg}`}
        >
          <Icon name={icon} filled className={`text-xl ${iconColor}`} />
        </div>
        <h3 className="text-base font-bold text-text-primary">{title}</h3>
      </div>
      <div className="text-sm text-text-secondary leading-relaxed">
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step component for "How it works"
// ---------------------------------------------------------------------------

function Step({
  number,
  icon,
  title,
  description,
}: {
  number: number;
  icon: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4">
      {/* Step number + connector line */}
      <div className="flex flex-col items-center">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-white shadow-lg shadow-primary-500/20">
          {number}
        </div>
        <div className="mt-2 w-px flex-1 bg-gray-200" />
      </div>
      {/* Content */}
      <div className="pb-8">
        <div className="mb-1 flex items-center gap-2">
          <Icon name={icon} className="text-lg text-primary-500" />
          <h3 className="text-base font-bold text-text-primary">{title}</h3>
        </div>
        <p className="text-sm text-text-secondary leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HowItWorksPage
// ---------------------------------------------------------------------------

export function HowItWorksPage() {
  return (
    <div>
      <PageHero
        icon="info"
        title="Know who you're really dealing with"
        subtitle={<>Before you meet a stranger from Marketplace, hand over keys to a subtenant, or agree to a first date — <BrandName /> lets you confirm they are exactly who they say they are.</>}
      />

      <div className="mx-auto max-w-2xl px-4 pb-12 sm:px-6">

        {/* Use case cards */}
        <div className="mb-12 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50">
              <Icon name="storefront" filled className="text-xl text-primary-500" />
            </div>
            <h3 className="text-sm font-bold text-text-primary">Marketplace &amp; Classifieds</h3>
            <p className="mt-1 text-xs text-text-secondary leading-relaxed">
              Buying or selling on Facebook Marketplace, Craigslist, or Gumtree? Send your trust link before you meet. Know the person showing up is real — not a scammer with a fake profile.
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
              <Icon name="home" filled className="text-xl text-amber-600" />
            </div>
            <h3 className="text-sm font-bold text-text-primary">Subletting &amp; Rentals</h3>
            <p className="mt-1 text-xs text-text-secondary leading-relaxed">
              Subletting your flat or taking in a lodger? Verify a prospective tenant's identity before handing over keys — no more hoping their ID photo matches reality.
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-rose-50">
              <Icon name="favorite" filled className="text-xl text-rose-500" />
            </div>
            <h3 className="text-sm font-bold text-text-primary">Dating &amp; Meeting Online</h3>
            <p className="mt-1 text-xs text-text-secondary leading-relaxed">
              Met someone on a dating app? Share a trust link before you agree to meet in person. Mutual verification means both of you know the other is genuinely who they claim to be.
            </p>
          </div>
        </div>

        {/* Privacy-first explanation */}
        <div className="mb-12 rounded-xl border border-primary-100 bg-primary-50 p-6">
          <div className="mb-3 flex items-center gap-2">
            <Icon name="shield" filled className="text-xl text-primary-500" />
            <h2 className="text-base font-bold text-text-primary">What you see — and what you don't</h2>
          </div>
          <p className="mb-4 text-sm text-text-secondary leading-relaxed">
            When someone completes your trust link, you never see their ID document, passport, phone number, email address, or any other sensitive information. That's by design.
          </p>
          <p className="mb-4 text-sm text-text-secondary leading-relaxed">
            What you <span className="font-semibold text-text-primary">do</span> get is a clear, verified confirmation: their name and the checks that passed — Government ID ✓, Phone ✓, Liveness ✓. Think of it like a background check that just tells you "cleared" — without handing you the file.
          </p>
          <p className="text-sm text-text-secondary leading-relaxed">
            The same applies in reverse. The person you're verifying never sees your documents either. Both sides are protected. Both sides can trust the result.
          </p>
        </div>

        {/* How it works steps */}
        <h2 className="mb-6 text-xl font-bold text-text-primary">How it works</h2>
        <div className="mb-12">
          <Step
            number={1}
            icon="app_registration"
            title="You verify yourself once"
            description="Sign up with your name, date of birth, and phone number, then complete a one-time identity check — a government ID scan and a quick liveness selfie. Once done, your identity is confirmed for good."
          />
          <Step
            number={2}
            icon="share"
            title="Send your trust link to someone"
            description="From your dashboard, copy your personal trust link and share it — by text, email, WhatsApp, or QR code. You can choose which checks the other person must complete (ID, phone, liveness, and more)."
          />
          <Step
            number={3}
            icon="badge"
            title="They complete their verification"
            description="The recipient follows your link and completes the identity checks you require. Their documents are verified privately behind the scenes — you never see them, and they never see yours."
          />
          <Step
            number={4}
            icon="check_circle"
            title="You both get a trusted confirmation"
            description="Once verified, you receive a clear result: their name and which checks passed. No raw documents, no phone numbers, no private data — just the confidence that they are genuinely who they say they are."
          />
          <Step
            number={5}
            icon="handshake"
            title="Move forward with confidence"
            description="Whether you're meeting in person, handing over keys, or continuing a conversation — you both know the other is real, verified, and accountable. That changes everything."
          />
        </div>

        {/* FAQ */}
        <h2 className="mb-4 text-xl font-bold text-text-primary">Common questions</h2>
        <div className="mb-12 flex flex-col gap-4">
          <SectionCard icon="visibility_off" title="Will I see their ID, phone number, or personal details?">
            <p>
              No. You will never see another user's identity documents, phone number, email address, date of birth, or SSN — even after they complete your trust link. All you receive is their verified name and a confirmation of which checks passed. The verification happens privately; only the result is shared.
            </p>
          </SectionCard>

          <SectionCard icon="lock" title="Can someone fake a verification?">
            <p>
              It's extremely difficult. Verification requires a live government-issued photo ID matched in real time against a liveness selfie — ruling out fake photos, pre-recorded videos, and AI-generated faces. The process is powered by the same bank-grade identity technology used by financial institutions.
            </p>
          </SectionCard>

          <SectionCard icon="timer" title="How long does it take?">
            <p>
              Your one-time identity verification takes around 2–5 minutes. After that, sharing your trust link and getting someone verified is instant — just copy the link and send it. The recipient's verification usually completes within 2–5 minutes too.
            </p>
          </SectionCard>

          <SectionCard icon="money_off" title="Is it free?">
            <p>
              Yes. Verifying your own identity and sharing trust links is completely free for individuals.
            </p>
          </SectionCard>

          <SectionCard icon="passkey" title="Do I need a password?">
            <p>
              No. <BrandName /> is fully passwordless. After your initial verification you sign in with Face ID, Touch ID, or a one-time SMS code — whichever you prefer. There are no passwords to create, forget, or get stolen.
            </p>
          </SectionCard>
        </div>

        {/* CTA */}
        <div className="rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 p-8 text-center text-white shadow-lg">
          <h2 className="text-2xl font-bold">Ready to verify yourself?</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-white/80">
            Get verified once, then know exactly who you're dealing with — every time.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-bold text-primary-600 shadow-sm transition-colors hover:bg-gray-50"
          >
            Get Verified
            <Icon name="arrow_forward" className="text-lg" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SecurityPage
// ---------------------------------------------------------------------------

export function SecurityPage() {
  return (
    <div>
      <PageHero
        icon="shield"
        title="Security & Privacy"
        subtitle="Your identity data is protected with bank-grade encryption and strict privacy controls at every step."
      />

      <div className="mx-auto max-w-3xl px-4 pb-12 sm:px-6">
        {/* Security pillars */}
        <div className="mb-12 grid gap-4 sm:grid-cols-2">
          <SectionCard
            icon="encrypted"
            iconBg="bg-green-50"
            iconColor="text-verified"
            title="End-to-End Encryption"
          >
            <p>
              All data in transit is encrypted using TLS 1.3. Sensitive identity
              data at rest is encrypted with AES-256. Your biometric data from
              the liveness check is processed in a secure enclave and never
              stored in raw form.
            </p>
          </SectionCard>

          <SectionCard
            icon="passkey"
            iconBg="bg-blue-50"
            iconColor="text-primary-500"
            title="Passwordless Authentication"
          >
            <p>
              We never store passwords because we don&apos;t use them. Your
              account is protected by WebAuthn passkeys (Face ID, Touch ID) —
              the same technology used by banks and government systems.
              Phishing-resistant by design.
            </p>
          </SectionCard>

          <SectionCard
            icon="visibility_off"
            iconBg="bg-purple-50"
            iconColor="text-purple-600"
            title="Zero-Knowledge Sharing"
          >
            <p>
              When you share your trust link, recipients see that you&apos;re
              verified — but they never see your actual documents, SSN, or
              biometric data. We share the proof, not the data.
            </p>
          </SectionCard>

          <SectionCard
            icon="gpp_good"
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
            title="OFAC & Compliance Screening"
          >
            <p>
              Every verification includes automated screening against the OFAC
              sanctions list and other regulatory databases. This protects the
              integrity of the entire <BrandName /> network.
            </p>
          </SectionCard>

          <SectionCard
            icon="face"
            iconBg="bg-rose-50"
            iconColor="text-rose-600"
            title="Liveness Detection"
          >
            <p>
              Our liveness check uses AWS Rekognition Face Liveness to ensure a
              real person is present — not a photo, video, or deepfake. The
              selfie is matched against your government ID in real time.
            </p>
          </SectionCard>

          <SectionCard
            icon="token"
            iconBg="bg-cyan-50"
            iconColor="text-cyan-600"
            title="Token Security"
          >
            <p>
              Authentication tokens (JWTs) are stored in memory only — never in
              browser storage where they could be stolen by malicious scripts.
              Tokens rotate automatically and expire after 6 hours.
            </p>
          </SectionCard>
        </div>

        {/* Data handling */}
        <h2 className="mb-4 text-xl font-bold text-text-primary">
          How We Handle Your Data
        </h2>
        <div className="mb-8 rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="divide-y divide-gray-100">
            {[
              {
                label: "Government ID images",
                detail:
                  "Processed for verification, then permanently deleted. Never stored on our servers.",
              },
              {
                label: "Liveness selfie",
                detail:
                  "Used for face matching only. Biometric template is discarded after verification.",
              },
              {
                label: "Phone number",
                detail:
                  "Used as your account identifier and for SMS authentication. Stored encrypted.",
              },
              {
                label: "Name & date of birth",
                detail:
                  "Stored for identity verification and displayed on your trust link profile.",
              },
              {
                label: "SSN (if provided)",
                detail:
                  "Used for identity verification and OFAC screening. Stored encrypted with restricted access.",
              },
              {
                label: "Connection history",
                detail:
                  "Records of who you've connected with. Visible only to you and the other party.",
              },
            ].map((item) => (
              <div key={item.label} className="px-5 py-3.5">
                <p className="text-sm font-semibold text-text-primary">
                  {item.label}
                </p>
                <p className="mt-0.5 text-xs text-text-secondary leading-relaxed">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance badges */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="mb-3 text-base font-bold text-text-primary">
            Compliance & Standards
          </h3>
          <div className="flex flex-wrap gap-3">
            {[
              "SOC 2 Type II",
              "GDPR Compliant",
              "CCPA Compliant",
              "KYC/AML",
              "WebAuthn L2",
              "TLS 1.3",
              "AES-256",
            ].map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-text-secondary"
              >
                <Icon name="check_circle" filled className="text-xs text-verified" />
                {badge}
              </span>
            ))}
          </div>
        </div>

        {/* Report concern CTA */}
        <div className="mt-8 text-center">
          <p className="text-sm text-text-secondary">
            Found a security vulnerability?
          </p>
          <a
            href="mailto:security@youareyou.io"
            className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors"
          >
            <Icon name="bug_report" className="text-lg" />
            Report to security@youareyou.io
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SupportPage
// ---------------------------------------------------------------------------

export function SupportPage() {
  return (
    <div>
      <PageHero
        icon="support_agent"
        title="Help & Support"
        subtitle={<>Find answers to common questions about <BrandName />, trust links, and identity verification.</>}
      />

      <div className="mx-auto max-w-3xl px-4 pb-12 sm:px-6">
        {/* Quick help categories */}
        <div className="mb-12 grid gap-4 sm:grid-cols-3">
          <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-50">
              <Icon name="link" className="text-2xl text-primary-500" />
            </div>
            <h3 className="text-sm font-bold text-text-primary">Trust Links</h3>
            <p className="mt-1 text-xs text-text-secondary">
              Creating, sharing, and managing your trust links and connections.
            </p>
          </div>
          <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
              <Icon name="badge" className="text-2xl text-amber-600" />
            </div>
            <h3 className="text-sm font-bold text-text-primary">Verification</h3>
            <p className="mt-1 text-xs text-text-secondary">
              How identity verification works and what each check confirms.
            </p>
          </div>
          <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
              <Icon name="shield" className="text-2xl text-verified" />
            </div>
            <h3 className="text-sm font-bold text-text-primary">Privacy & Security</h3>
            <p className="mt-1 text-xs text-text-secondary">
              How your documents and data are protected at every step.
            </p>
          </div>
        </div>

        {/* Trust Link FAQs */}
        <h2 className="mb-4 text-xl font-bold text-text-primary">Trust Links</h2>
        <div className="mb-10 flex flex-col gap-4">
          <SectionCard icon="link" title={<>What is a <BrandName /> Trust Link?</>}>
            <p>
              A trust link is a unique URL you share with someone when you want to confirm they are who they say they are.
              When the recipient clicks your link, they are guided through an identity verification process. Once complete,
              both of you receive a confirmed connection — you know they&apos;re real and verified, and they know you are too.
            </p>
          </SectionCard>

          <SectionCard icon="send" title="How do I send a trust link?">
            <p className="mb-2">
              From your dashboard, your personal trust link is shown at the top of the screen. You can:
            </p>
            <ul className="list-inside list-disc space-y-1">
              <li>Tap <strong>Copy Link</strong> and paste it into a text, email, or chat</li>
              <li>Tap the QR code icon to show a scannable code in person</li>
              <li>Use the settings gear to customise which verifications the recipient must complete before the link activates</li>
            </ul>
            <p className="mt-2">
              Each link you share is unique and traceable — you can see when it was used from your activity feed.
            </p>
          </SectionCard>

          <SectionCard icon="settings" title="What verifications can I require?">
            <p className="mb-2">
              Tap the gear icon next to your link to choose what the recipient must complete. Options include:
            </p>
            <ul className="list-inside list-disc space-y-1">
              <li><strong>Government ID</strong> — driver&apos;s licence or passport scan</li>
              <li><strong>Liveness check</strong> — a real-time selfie to confirm a live person</li>
              <li><strong>Phone verification</strong> — confirmed mobile number</li>
              <li><strong>Email verification</strong> — confirmed email address</li>
              <li><strong>Physical address</strong> — verified home or business address</li>
              <li><strong>SSN verification</strong> — Social Security Number (US only)</li>
              <li><strong>OFAC check</strong> — sanctions list screening</li>
            </ul>
            <p className="mt-2">
              Your settings are saved and applied to every new link you create going forward.
            </p>
          </SectionCard>

          <SectionCard icon="visibility_off" title="Will I see the other person's documents?">
            <p>
              No — and they won&apos;t see yours either. All identity documents are verified privately behind the scenes
              by our verification partner. Neither party ever sees the other&apos;s ID, selfie, SSN, or any other sensitive
              document. You simply receive a trusted confirmation that the required checks have passed.
              The proof is shared — the documents never are.
            </p>
          </SectionCard>

          <SectionCard icon="pending" title="I sent a link but nothing has happened yet">
            <p className="mb-2">
              The recipient needs to open your link and complete the verification steps you set. A few things to check:
            </p>
            <ul className="list-inside list-disc space-y-1">
              <li>Make sure they received the link — try resending it via a different channel</li>
              <li>Verification typically takes 2–5 minutes once they start</li>
              <li>Once they finish, you&apos;ll see the connection appear in your <strong>Contacts</strong> tab</li>
              <li>Pending requests are visible under the <strong>Requests</strong> tab</li>
            </ul>
          </SectionCard>

          <SectionCard icon="cancel" title="Can I cancel or revoke a trust link?">
            <p>
              Yes. Pending connections can be cancelled from the <strong>Requests</strong> tab at any time before the
              recipient completes verification. Once a connection is verified, it is a mutual confirmed link — both
              parties agreed to connect. If you need to remove a verified connection for safety reasons, contact our
              support team.
            </p>
          </SectionCard>
        </div>

        {/* Verification FAQs */}
        <h2 className="mb-4 text-xl font-bold text-text-primary">Identity Verification</h2>
        <div className="mb-10 flex flex-col gap-4">
          <SectionCard icon="how_to_reg" title="What happens during verification?">
            <p className="mb-2">
              When someone opens your trust link, they are guided through a secure identity check. Depending on your settings, this may include:
            </p>
            <ul className="list-inside list-disc space-y-1">
              <li>Scanning the front (and optionally back) of a government-issued photo ID</li>
              <li>Completing a short liveness check — a real-time selfie matched against the ID</li>
              <li>Confirming their phone number and/or email address</li>
            </ul>
            <p className="mt-2">
              The process takes around 2–5 minutes. Results are confirmed automatically — no manual review required in most cases.
            </p>
          </SectionCard>

          <SectionCard icon="badge" title="Verification failed or is stuck pending">
            <p className="mb-2">Common reasons verification fails:</p>
            <ul className="list-inside list-disc space-y-1">
              <li>Blurry ID photo or glare — retry in good, even lighting</li>
              <li>Expired ID — only valid, current IDs are accepted</li>
              <li>Liveness check failed — face must be well-lit, centred, and unobscured</li>
              <li>Name on ID doesn&apos;t match registration details — contact support</li>
            </ul>
            <p className="mt-2">
              If a verification has been stuck in pending for more than 24 hours, please contact us.
            </p>
          </SectionCard>

          <SectionCard icon="lock" title="Is my data safe during verification?">
            <p>
              Yes. Identity documents are processed by our verification partner (MojoVerify) using bank-grade encryption.
              ID images are used solely for verification and are not stored on our servers after processing.
              Liveness selfies are matched in real time and the biometric data is discarded immediately after.
              No sensitive document is ever visible to another user.
            </p>
          </SectionCard>
        </div>

        {/* Account & Login FAQs */}
        <h2 className="mb-4 text-xl font-bold text-text-primary">Account & Login</h2>
        <div className="mb-10 flex flex-col gap-4">
          <SectionCard icon="sms" title="I didn't receive my SMS login code">
            <p className="mb-2">SMS codes can take up to 2 minutes. If you haven&apos;t received one:</p>
            <ul className="list-inside list-disc space-y-1">
              <li>Check that your phone number includes the correct country code</li>
              <li>Check your spam or blocked messages folder</li>
              <li>SMS requires a cellular signal — it will not arrive over Wi-Fi calling on all carriers</li>
              <li>VoIP numbers (Google Voice, Skype etc.) may not receive SMS — use a mobile number</li>
              <li>Tap <strong>Resend code</strong> on the login page to request a new one</li>
            </ul>
          </SectionCard>

          <SectionCard icon="passkey" title="My Face ID / Touch ID passkey isn't working">
            <p className="mb-2">Passkeys are bound to the device and browser where they were created. Try:</p>
            <ul className="list-inside list-disc space-y-1">
              <li>Using the same browser you used when you first set up the passkey</li>
              <li>Checking that biometrics are enabled in your device settings</li>
              <li>If you&apos;re on a new device, sign in with your SMS code first, then register a new passkey from Settings</li>
            </ul>
          </SectionCard>

          <SectionCard icon="delete" title="How do I delete my account?">
            <p>
              Email{" "}
              <a href="mailto:support@youareyou.io" className="font-semibold text-primary-500 hover:text-primary-600">
                support@youareyou.io
              </a>{" "}
              from the phone number associated with your account. We process deletion requests within 30 days
              in compliance with GDPR and CCPA requirements. All personal data including verification records
              will be permanently removed.
            </p>
          </SectionCard>
        </div>

        {/* Contact section */}
        <h2 className="mb-4 text-xl font-bold text-text-primary">Still Need Help?</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <a
            href="mailto:support@youareyou.io"
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-50">
              <Icon name="mail" className="text-2xl text-primary-500" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-text-primary">Email Support</h3>
              <p className="mt-0.5 text-xs text-text-secondary">support@youareyou.io</p>
              <p className="mt-0.5 text-[11px] text-text-muted">Response within 24 hours</p>
            </div>
          </a>

          <a
            href="mailto:security@youareyou.io"
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-50">
              <Icon name="shield" className="text-2xl text-error" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-text-primary">Security Issues</h3>
              <p className="mt-0.5 text-xs text-text-secondary">security@youareyou.io</p>
              <p className="mt-0.5 text-[11px] text-text-muted">For vulnerability reports</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PrivacyPage — placeholder privacy policy
// ---------------------------------------------------------------------------

export function PrivacyPage() {
  return (
    <div>
      <PageHero
        icon="policy"
        title="Privacy Policy"
        subtitle="How we collect, use, and protect your personal information."
      />

      <div className="mx-auto max-w-2xl px-4 pb-12 sm:px-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          <p className="mb-6 text-sm text-text-secondary leading-relaxed">
            Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </p>

          <div className="prose-sm space-y-6 text-sm text-text-secondary leading-relaxed">
            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                1. Information We Collect
              </h3>
              <p>
                We collect the following information when you create a{" "}
                <BrandName /> account and complete identity verification:
              </p>
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>Full name and date of birth</li>
                <li>Phone number (used as your account identifier)</li>
                <li>Government-issued ID (processed for verification, then deleted)</li>
                <li>Liveness selfie (used for face matching, biometric template discarded after verification)</li>
                <li>Social Security Number (if provided, for identity verification and OFAC screening)</li>
                <li>Device information and IP address (for security and fraud prevention)</li>
              </ul>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                2. How We Use Your Information
              </h3>
              <p>Your information is used to:</p>
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>Verify your identity through our verification process</li>
                <li>Create and maintain your <BrandName /> account</li>
                <li>Generate your trust link and verified profile</li>
                <li>Facilitate connections between verified users</li>
                <li>Send authentication codes and account notifications via SMS</li>
                <li>Comply with legal and regulatory requirements</li>
              </ul>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                3. Data Sharing
              </h3>
              <p>
                We do not sell your personal information. We share data only:
              </p>
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>With your explicit consent (e.g., when you share your trust link)</li>
                <li>With our identity verification partners</li>
                <li>When required by law or legal process</li>
                <li>To protect the safety and security of our users and platform</li>
              </ul>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                4. Your Rights
              </h3>
              <p>You have the right to:</p>
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>Access your personal data</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your account and data</li>
                <li>Object to processing of your data</li>
                <li>Export your data in a portable format</li>
              </ul>
              <p className="mt-2">
                To exercise these rights, contact{" "}
                <a
                  href="mailto:privacy@youareyou.io"
                  className="font-semibold text-primary-500 hover:text-primary-600"
                >
                  privacy@youareyou.io
                </a>
                .
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                5. Contact
              </h3>
              <p>
                For privacy-related questions, contact our Data Protection
                Officer at{" "}
                <a
                  href="mailto:privacy@youareyou.io"
                  className="font-semibold text-primary-500 hover:text-primary-600"
                >
                  privacy@youareyou.io
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TermsPage — placeholder terms of service
// ---------------------------------------------------------------------------

export function TermsPage() {
  return (
    <div>
      <PageHero
        icon="gavel"
        title="Terms of Service"
        subtitle={<>The terms governing your use of the <BrandName /> platform.</>}
      />

      <div className="mx-auto max-w-2xl px-4 pb-12 sm:px-6">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          <p className="mb-6 text-sm text-text-secondary leading-relaxed">
            Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </p>

          <div className="prose-sm space-y-6 text-sm text-text-secondary leading-relaxed">
            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                1. Acceptance of Terms
              </h3>
              <p>
                By creating a <BrandName /> account or using our services, you agree
                to be bound by these Terms of Service. If you do not agree, do
                not use the service.
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                2. Eligibility
              </h3>
              <p>
                You must be at least 18 years old and capable of forming a
                binding contract to use <BrandName />. By registering, you
                represent that you are providing accurate, truthful information
                about your identity.
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                3. Identity Verification
              </h3>
              <p>
                <BrandName /> requires identity verification for all users.
                You agree to:
              </p>
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>Provide accurate and complete information during registration</li>
                <li>Submit a valid, unexpired government-issued photo ID</li>
                <li>Complete the liveness verification process honestly</li>
                <li>Not impersonate another person or use a fraudulent identity</li>
              </ul>
              <p className="mt-2">
                Fraudulent verification attempts will result in immediate
                account termination and may be reported to law enforcement.
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                4. Trust Links & Connections
              </h3>
              <p>
                Your trust link is a representation of your verified identity.
                You agree not to:
              </p>
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>Use trust links for harassment, fraud, or illegal purposes</li>
                <li>Spam or send unsolicited trust link requests</li>
                <li>Misrepresent the nature of a connection request</li>
              </ul>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                5. Account Security
              </h3>
              <p>
                You are responsible for maintaining the security of your
                devices and authentication methods (passkeys, phone number).
                Notify us immediately if you believe your account has been
                compromised.
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                6. Termination
              </h3>
              <p>
                We may suspend or terminate your account if you violate these
                terms, engage in fraudulent activity, or if required by law.
                You may delete your account at any time by contacting{" "}
                <a
                  href="mailto:support@youareyou.io"
                  className="font-semibold text-primary-500 hover:text-primary-600"
                >
                  support@youareyou.io
                </a>
                .
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                7. Limitation of Liability
              </h3>
              <p>
                <BrandName /> provides identity verification services on an
                &quot;as-is&quot; basis. We do not guarantee the accuracy of
                third-party verification data. We are not liable for decisions
                made by third parties based on your verified status.
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-base font-bold text-text-primary">
                8. Contact
              </h3>
              <p>
                For questions about these terms, contact{" "}
                <a
                  href="mailto:legal@youareyou.io"
                  className="font-semibold text-primary-500 hover:text-primary-600"
                >
                  legal@youareyou.io
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
